'use client';

import React, { useState } from 'react';
import { Search, AlertTriangle, Package, TrendingDown } from 'lucide-react';
import { MOCK_FEED_STOCK, FeedStock, StockStatus } from './feedData';

const STATUS_STYLES: Record<StockStatus, string> = {
  'In Stock': 'bg-emerald-100 text-emerald-700',
  'Low Stock': 'bg-amber-100 text-amber-700',
  'Out of Stock': 'bg-red-100 text-red-700',
  'Ordered': 'bg-blue-100 text-blue-700',
};

function StockBar({ qty, max, reorder }: { qty: number; max: number; reorder: number }) {
  const pct = Math.min((qty / max) * 100, 100);
  const reorderPct = (reorder / max) * 100;
  const color = qty === 0 ? '#ef4444' : qty <= reorder ? '#f59e0b' : '#10b981';
  return (
    <div className="relative w-full h-2 bg-gray-100 rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      <div className="absolute top-0 h-full w-0.5 bg-gray-400 opacity-60" style={{ left: `${reorderPct}%` }} />
    </div>
  );
}

export default function FeedStockTab() {
  const [stock] = useState<FeedStock[]>(MOCK_FEED_STOCK);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterFarm, setFilterFarm] = useState('all');

  const farms = Array.from(new Set(stock.map(s => s.farm)));

  const filtered = stock.filter(s => {
    const q = search.toLowerCase();
    const matchSearch = !q || s.feedTypeName.toLowerCase().includes(q) || s.feedCode.toLowerCase().includes(q) || s.store.toLowerCase().includes(q);
    const matchStatus = filterStatus === 'all' || s.status === filterStatus;
    const matchFarm = filterFarm === 'all' || s.farm === filterFarm;
    return matchSearch && matchStatus && matchFarm;
  });

  const totalValue = filtered.reduce((s, f) => s + f.totalValue, 0);
  const lowCount = filtered.filter(f => f.status === 'Low Stock').length;
  const outCount = filtered.filter(f => f.status === 'Out of Stock').length;

  return (
    <div>
      {/* Alert strip */}
      {(lowCount > 0 || outCount > 0) && (
        <div className="flex flex-wrap gap-3 mb-4">
          {outCount > 0 && (
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
              <AlertTriangle size={14} className="flex-shrink-0" />
              <span><strong>{outCount}</strong> feed type{outCount > 1 ? 's' : ''} out of stock — reorder immediately</span>
            </div>
          )}
          {lowCount > 0 && (
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-700">
              <TrendingDown size={14} className="flex-shrink-0" />
              <span><strong>{lowCount}</strong> feed type{lowCount > 1 ? 's' : ''} below reorder level</span>
            </div>
          )}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search stock…" className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300" style={{ backgroundColor: 'var(--card-bg)' }} />
        </div>
        <select value={filterFarm} onChange={e => setFilterFarm(e.target.value)} className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none" style={{ backgroundColor: 'var(--card-bg)' }}>
          <option value="all">All Farms</option>
          {farms.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none" style={{ backgroundColor: 'var(--card-bg)' }}>
          <option value="all">All Status</option>
          {(['In Stock','Low Stock','Out of Stock','Ordered'] as StockStatus[]).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[
          { label: 'Total Stock Items', value: filtered.length, icon: Package, color: 'text-indigo-600', bg: 'bg-indigo-50' },
          { label: 'Total Stock Value', value: `UGX ${(totalValue / 1000000).toFixed(1)}M`, icon: Package, color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'Low Stock', value: lowCount, icon: TrendingDown, color: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'Out of Stock', value: outCount, icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
        ].map(card => (
          <div key={card.label} className="flex items-center gap-3 p-3 rounded-xl border border-gray-100" style={{ backgroundColor: 'var(--card-bg)' }}>
            <div className={`p-2 rounded-lg ${card.bg}`}><card.icon size={16} className={card.color} /></div>
            <div><div className="text-xs text-gray-500">{card.label}</div><div className="text-base font-bold text-gray-800">{card.value}</div></div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-100">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100" style={{ backgroundColor: 'var(--surface)' }}>
              {['Feed Type','Code','Farm / Store','Stock Level','Qty (kg)','Reorder Level','Max Capacity','Unit Cost','Total Value','Batch','Expiry','Status'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(s => (
              <tr key={s.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors" style={{ backgroundColor: 'var(--card-bg)' }}>
                <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap">{s.feedTypeName}</td>
                <td className="px-4 py-3 font-mono text-xs text-indigo-600">{s.feedCode}</td>
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                  <div className="text-xs font-medium">{s.farm}</div>
                  <div className="text-xs text-gray-400">{s.store}</div>
                </td>
                <td className="px-4 py-3 min-w-[120px]">
                  <StockBar qty={s.quantityKg} max={s.maxCapacityKg} reorder={s.reorderLevelKg} />
                  <div className="text-xs text-gray-400 mt-1">{Math.round((s.quantityKg / s.maxCapacityKg) * 100)}% capacity</div>
                </td>
                <td className="px-4 py-3 font-semibold text-gray-800">{s.quantityKg.toLocaleString()}</td>
                <td className="px-4 py-3 text-gray-500">{s.reorderLevelKg.toLocaleString()}</td>
                <td className="px-4 py-3 text-gray-500">{s.maxCapacityKg.toLocaleString()}</td>
                <td className="px-4 py-3 text-right">{s.unitCost.toLocaleString()}</td>
                <td className="px-4 py-3 text-right font-medium text-gray-700">{(s.totalValue / 1000).toFixed(0)}K</td>
                <td className="px-4 py-3 font-mono text-xs text-gray-500">{s.batchNumber}</td>
                <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{s.expiryDate}</td>
                <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${STATUS_STYLES[s.status]}`}>{s.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="py-12 text-center text-gray-400 text-sm">No stock records found</div>}
      </div>
    </div>
  );
}
